import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const STATS = [
  { label: "CLINICS ACTIVE", value: "500+" },
  { label: "TOKENS ISSUED", value: "1M+" },
  { label: "AVG. WAIT REDUCTION", value: "15m" },
  { label: "USER SATISFACTION", value: "98%" },
];

const VALUES = [
  {
    title: "Clinical Authority",
    description: "Our solutions are built by doctors, for doctors, ensuring medical accuracy and regulatory compliance at every step.",
    icon: (
      <svg className="w-6 h-6 text-[#a55e43]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: "Patient-Centricity",
    description: "We prioritize the patient journey, designing intuitive interfaces that reduce anxiety and simplify the healthcare process.",
    icon: (
      <svg className="w-6 h-6 text-[#a55e43]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    title: "Tech Innovation",
    description: "Leveraging AI and real-time data analytics to predict clinic traffic and optimize provider schedules dynamically.",
    icon: (
      <svg className="w-6 h-6 text-[#a55e43]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
];

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-[#faf9f7] text-[#1a3c34]">
      <Navbar />

      <section className="bg-[#1a3c34] text-white pt-24 pb-32 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <span className="inline-block px-4 py-1 rounded-full bg-[#2d5a45] text-[#d1e7d1] text-xs font-bold uppercase tracking-widest mb-6">
            Our Story
          </span>
          <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight font-display tracking-tight">
            Humanizing Healthcare Through <br className="hidden md:block" />
            <span className="text-[#a55e43]">Intelligent Technology</span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
            We bridge the gap between complex medical operations and compassionate patient care,
            creating a seamless experience for clinics and patients alike.
          </p>
        </div>
      </section>

      <section className="px-6 -mt-16 pb-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center bg-white rounded-3xl shadow-xl overflow-hidden border border-[#dadad8]">
          <div className="aspect-video lg:aspect-square bg-[#f4f4f2] flex items-center justify-center p-8">
            <div className="w-full h-full rounded-2xl bg-[#1a3c34]/5 border-2 border-dashed border-[#1a3c34]/20 flex items-center justify-center text-[#1a3c34]/30 italic">
              Platform Interface Visual
            </div>
          </div>
          <div className="p-8 md:p-16 space-y-12">
            <div>
              <h2 className="text-3xl font-bold mb-4 font-display tracking-tight">Our Mission</h2>
              <p className="text-[#2d5a45] text-lg leading-relaxed">
                To empower healthcare providers with cutting-edge digital infrastructure that eliminates
                operational friction, allowing them to focus on what matters most: saving lives and improving patient well-being.
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-4 font-display tracking-tight">Our Vision</h2>
              <p className="text-[#2d5a45] text-lg leading-relaxed">
                A world where healthcare is universally accessible, wait-times are a thing of the past,
                and medical professionals are equipped with data-driven tools that make every interaction meaningful.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-24 bg-[#f4f4f2]/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 font-display tracking-tight">What Drives Us</h2>
            <p className="text-[#2d5a45]">The principles that guide every line of code we write.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map((val, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-[#dadad8] shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#a55e43]/10 rounded-xl flex items-center justify-center mb-6">
                  {val.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{val.title}</h3>
                <p className="text-[#2d5a45] leading-relaxed">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1a3c34] py-20 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-5xl font-bold text-white mb-2 font-display tracking-tight">{stat.value}</div>
              <div className="text-xs md:text-sm font-bold text-white/50 tracking-widest uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 py-24">
        <div className="max-w-7xl mx-auto bg-white rounded-3xl p-8 md:p-20 text-center border border-[#dadad8] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#a55e43]/5 rounded-full -mr-32 -mt-32" />
          <h2 className="text-4xl font-bold mb-6 font-display tracking-tight">Join the Healthcare Revolution</h2>
          <p className="text-lg text-[#2d5a45] max-w-2xl mx-auto mb-10 leading-relaxed">
            Whether you're a clinician frustrated with legacy systems or a developer looking for impactful work,
            we want to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link to="/careers" className="inline-block px-10 py-4 bg-[#1a3c34] text-white font-bold rounded-xl hover:bg-[#15312a] transition-all">
              View Openings
            </Link>
            <button className="px-10 py-4 border-2 border-[#a55e43] text-[#a55e43] font-bold rounded-xl hover:bg-[#a55e43]/5 transition-all">
              Partner With Us
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
